import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiZap, 
  FiShield, 
  FiTrendingUp, 
  FiUsers, 
  FiCheckCircle,
  FiArrowRight,
  FiMonitor,
  FiAlertTriangle
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './APM.css';

const APM = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/apm', {
      page_title: 'LogCrest APM - Application Performance Monitoring',
      page_category: 'apm'
    });
  }, []);

  const features = [
    {
      icon: <FiZap />,
      title: 'eBPF-Powered Monitoring',
      description: 'Non-intrusive application monitoring with eBPF technology for deep visibility without performance overhead.'
    },
    {
      icon: <FiShield />,
      title: 'Real-time Performance Insights',
      description: 'Monitor application behavior and service health with real-time performance metrics and analytics.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Agentless Deployment',
      description: 'No language agents required - get instant observability across your entire application stack.'
    },
    {
      icon: <FiUsers />,
      title: 'AI-Powered Root Cause Analysis',
      description: 'AI analyzes telemetry data to quickly identify root causes and provide actionable recommendations.'
    }
  ];

  const benefits = [
    'Non-intrusive eBPF-based monitoring',
    'Real-time application behavior insights',
    'Agentless deployment across tech stacks',
    'AI-powered root cause analysis',
    'Enhanced security with kernel-level observability',
    'Up to 10x cost reduction vs traditional solutions'
  ];

  return (
    <div className="agent-sre-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Application Performance Monitoring (APM)</h1>
            <p className="hero-subtitle">
              Real-time insights into application behavior and performance with eBPF-powered monitoring
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                  <FiArrowRight />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Powerful APM Capabilities</h2>
            <p>Monitor your applications with industry-leading performance insights</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our APM Solution?</h2>
              <p>
                Our eBPF-powered APM provides instant visibility into your applications without 
                requiring code changes or performance overhead.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>APM Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiMonitor />
                    <span>Response Time: 120ms</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>Throughput: 1.2K req/s</span>
                  </div>
                  <div className="metric">
                    <FiAlertTriangle />
                    <span>Error Rate: 0.1%</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Monitor Your Applications?</h2>
            <p>Get started with our APM solution and see the difference eBPF-powered monitoring makes.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
                <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default APM;